import React, { useState } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Drawer, List, ListItem, ListItemIcon, ListItemText, CssBaseline, Box, IconButton } from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import WidgetsIcon from '@mui/icons-material/Widgets';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import TenantsPage from './pages/TenantsPage';
import PlatformsPage from './pages/PlatformsPage';
import PlatformCredentialsPage from './pages/PlatformCredentialsPage';
import DomainManagementPage from './pages/DomainManagementPage';
import LoginPage from './pages/LoginPage';
import { isAuthenticatedAdmin, getCurrentUser } from './api/auth';
import axios from 'axios';
import AdminsPage from './pages/AdminsPage';
import TenantUsersPage from './pages/TenantUsersPage';
import ListItemButton from '@mui/material/ListItemButton';
import LogoutIcon from '@mui/icons-material/Logout';
import PurchasesPage from './pages/PurchasesPage';
import DepositsPage from './pages/DepositsPage';
import RedeemsPage from './pages/RedeemsPage';
import AffiliateTrackingPage from './pages/AffiliateTrackingPage';
import TenantBonusConfigPage from './pages/TenantBonusConfigPage';
import PlatformRedeemConfigPage from './pages/PlatformRedeemConfigPage';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import GamePromotionsPage from './pages/GamePromotionsPage';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import BarChartIcon from '@mui/icons-material/BarChart';
import DashboardMetrics from './components/DashboardMetrics';

const drawerWidth = 220;

const theme = createTheme();

const TenantAdminPanel: React.FC<{ tenantId: number }> = ({ tenantId }) => {
  const [selectedPage, setSelectedPage] = useState<'users' | 'purchases' | 'deposits' | 'redeems' | 'affiliate' | 'bonus-config' | 'game-promotions' | 'platform-credentials' | 'redeem-config' | 'analytics'>('users');
  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { 
            width: drawerWidth, 
            boxSizing: 'border-box',
            overflow: 'auto'
          },
        }}
      >
        <Toolbar />
        <DashboardMetrics variant="sidebar" tenantId={tenantId} />
        <List>
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedPage === 'users'}
              onClick={() => setSelectedPage('users')}
            >
              <ListItemIcon><ApartmentIcon /></ListItemIcon>
              <ListItemText primary="Users" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedPage === 'purchases'}
              onClick={() => setSelectedPage('purchases')}
            >
              <ListItemIcon><WidgetsIcon /></ListItemIcon>
              <ListItemText primary="Purchases" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedPage === 'deposits'}
              onClick={() => setSelectedPage('deposits')}
            >
              <ListItemIcon><WidgetsIcon /></ListItemIcon>
              <ListItemText primary="Deposits" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedPage === 'redeems'}
              onClick={() => setSelectedPage('redeems')}
            >
              <ListItemIcon><WidgetsIcon /></ListItemIcon>
              <ListItemText primary="Auto-Redeems" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedPage === 'affiliate'}
              onClick={() => setSelectedPage('affiliate')}
            >
              <ListItemIcon><PeopleIcon /></ListItemIcon>
              <ListItemText primary="Affiliate Tracking" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedPage === 'bonus-config'}
              onClick={() => setSelectedPage('bonus-config')}
            >
              <ListItemIcon><SettingsIcon /></ListItemIcon>
              <ListItemText primary="Bonus Configuration" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedPage === 'game-promotions'}
              onClick={() => setSelectedPage('game-promotions')}
            >
              <ListItemIcon><LocalOfferIcon /></ListItemIcon>
              <ListItemText primary="Game Promotions" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedPage === 'platform-credentials'}
              onClick={() => setSelectedPage('platform-credentials')}
            >
              <ListItemIcon><SettingsIcon /></ListItemIcon>
              <ListItemText primary="Platform Credentials" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedPage === 'redeem-config'}
              onClick={() => setSelectedPage('redeem-config')}
            >
              <ListItemIcon><SettingsIcon /></ListItemIcon>
              <ListItemText primary="Redeem Settings" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedPage === 'analytics'}
              onClick={() => setSelectedPage('analytics')}
            >
              <ListItemIcon><BarChartIcon /></ListItemIcon>
              <ListItemText primary="Analytics" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
                <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}>
            <Toolbar />
            <Box sx={{ px: 2 }}>
              {selectedPage === 'users' && <TenantUsersPage tenantId={tenantId} />}
              {selectedPage === 'purchases' && <PurchasesPage tenantId={tenantId} />}
              {selectedPage === 'deposits' && <DepositsPage tenantId={tenantId} />}
              {selectedPage === 'redeems' && <RedeemsPage tenantId={tenantId} />}
              {selectedPage === 'affiliate' && <AffiliateTrackingPage tenantId={tenantId} />}
              {selectedPage === 'bonus-config' && <TenantBonusConfigPage />}
              {selectedPage === 'game-promotions' && <GamePromotionsPage tenantId={tenantId} />}
              {selectedPage === 'platform-credentials' && <PlatformCredentialsPage />}
              {selectedPage === 'redeem-config' && <PlatformRedeemConfigPage />}
              {selectedPage === 'analytics' && <AnalyticsDashboardPage tenantId={tenantId} />}
            </Box>
          </Box>
    </Box>
  );
};

function App() {
  const [loggedIn, setLoggedIn] = useState(isAuthenticatedAdmin());
  const [user, setUser] = useState(getCurrentUser());
  const navigate = useNavigate?.() || (() => {});
  const location = useLocation?.() || { pathname: '/' };

  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    setUser(getCurrentUser());
  }, [loggedIn]);

  if (!loggedIn) {
    return <LoginPage onLogin={u => { setLoggedIn(true); setUser(u); }} />;
  }

  // Super admin: full panel
  if (user?.role?.is_super_admin) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ display: 'flex' }}>
          <CssBaseline />
          <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
            <Toolbar>
              <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                RJJ Management
              </Typography>
              <DashboardMetrics variant="header" />
              <IconButton color="inherit" onClick={() => {
                localStorage.clear();
                window.location.href = '/';
              }} title="Logout">
                <LogoutIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          <Drawer
            variant="permanent"
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              [`& .MuiDrawer-paper`]: { 
                width: drawerWidth, 
                boxSizing: 'border-box',
                overflow: 'auto'
              },
            }}
          >
            <Toolbar />
            <DashboardMetrics variant="sidebar" />
            <List>
              <ListItem button component={Link as any} to="/tenants">
                <ListItemIcon><ApartmentIcon /></ListItemIcon>
                <ListItemText primary="Tenants" />
              </ListItem>
              <ListItem button component={Link as any} to="/platforms">
                <ListItemIcon><WidgetsIcon /></ListItemIcon>
                <ListItemText primary="Platforms" />
              </ListItem>
              <ListItem button component={Link as any} to="/platform-credentials">
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText primary="Platform Credentials" />
              </ListItem>
              <ListItem button component={Link as any} to="/bonus-config">
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText primary="Bonus Configuration" />
              </ListItem>
              <ListItem button component={Link as any} to="/redeem-config">
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText primary="Redeem Settings" />
              </ListItem>
              <ListItem button component={Link as any} to="/admins">
                <ListItemIcon><ApartmentIcon /></ListItemIcon>
                <ListItemText primary="Admins" />
              </ListItem>
              <ListItem button component={Link as any} to="/users">
                <ListItemIcon><ApartmentIcon /></ListItemIcon>
                <ListItemText primary="Users" />
              </ListItem>
              <ListItem button component={Link as any} to="/purchases">
                <ListItemIcon><WidgetsIcon /></ListItemIcon>
                <ListItemText primary="Purchases" />
              </ListItem>
              <ListItem button component={Link as any} to="/deposits">
                <ListItemIcon><WidgetsIcon /></ListItemIcon>
                <ListItemText primary="Deposits" />
              </ListItem>
              <ListItem button component={Link as any} to="/redeems">
                <ListItemIcon><WidgetsIcon /></ListItemIcon>
                <ListItemText primary="Auto-Redeems" />
              </ListItem>
              <ListItem button component={Link as any} to="/analytics">
                <ListItemIcon><BarChartIcon /></ListItemIcon>
                <ListItemText primary="Analytics" />
              </ListItem>
            </List>
          </Drawer>
          <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}>
            <Toolbar />
            <Box sx={{ px: 2 }}>
              <Routes>
                <Route path="/tenants" element={<TenantsPage onTenantClick={tid => navigate(`/tenant/${tid}/admin`)} />} />
                <Route path="/platforms" element={<PlatformsPage />} />
                <Route path="/platform-credentials" element={<PlatformCredentialsPage />} />
                <Route path="/bonus-config" element={<TenantBonusConfigPage />} />
                <Route path="/redeem-config" element={<PlatformRedeemConfigPage />} />
                <Route path="/tenant/:tenantId/admin" element={<TenantAdminPanel tenantId={parseInt(location.pathname.split('/')[2])} />} />
                <Route path="/admins" element={<AdminsPage />} />
                <Route path="/domain-management" element={<DomainManagementPage />} />
                <Route path="/purchases" element={<PurchasesPage />} />
                <Route path="/users" element={<TenantUsersPage />} />
                <Route path="/deposits" element={<DepositsPage />} />
                <Route path="/redeems" element={<RedeemsPage />} />
                <Route path="/analytics" element={<AnalyticsDashboardPage />} />
                <Route path="*" element={<TenantsPage onTenantClick={tid => navigate(`/tenant/${tid}/admin`)} />} />
              </Routes>
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  // Normal admin: only their tenant panel
  if (user?.role?.name === 'admin') {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ display: 'flex' }}>
          <CssBaseline />
          <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
            <Toolbar>
              <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                Tenant Admin Panel
              </Typography>
              <DashboardMetrics variant="header" tenantId={user.tenant_id} />
              <IconButton color="inherit" onClick={() => {
                localStorage.clear();
                window.location.href = '/';
              }} title="Logout">
                <LogoutIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}>
            <Toolbar />
            <Box sx={{ px: 2 }}>
              <TenantAdminPanel tenantId={user.tenant_id} />
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  // Fallback
  return <div>Unauthorized</div>;
}

export default App;
